import type { PropType } from "vue";

import { computed, reactive, toRef } from "vue";

import type { DefineProps } from "@/lib/composables/componentComposable";

import { size } from "@/lib/components/logic/atoms/input/props";
import {
  emitsDefinition,
  propsDefinition,
} from "@/lib/composables/componentComposable";

const scoped = propsDefinition({
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  showValidState: { type: Boolean, default: true },
  prefixIcon: {
    type: String,
    required: false,
  },
  prefixIconButton: { type: Boolean, default: false },
  suffixIcon: {
    type: String,
    required: false,
  },
  suffixIconButton: { type: Boolean, default: false },
  size,
});

const props = propsDefinition({
  ...scoped,
  valid: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  color: { type: String, default: "gray" },
  location: {
    type: String as PropType<"prefix" | "suffix">,
    required: false,
  },
  icon: {
    type: String,
    required: false,
  },
});

const emits = emitsDefinition(["clickPrefixIcon", "clickSuffixIcon"]);

type UseInputIconAtomProps = DefineProps<typeof props>;
type UseInputIconAtomEmit = (
  event: "clickPrefixIcon" | "clickSuffixIcon",
) => void;

function use(props: UseInputIconAtomProps, emit: UseInputIconAtomEmit) {
  const suffixIcon = computed(() => {
    if (props.loading) {
      return "fa-regular fa-spinner-third fa-spin";
    }
    if (props.suffixIcon) {
      return props.suffixIcon;
    }
    if (props.valid && props.showValidState) {
      return "fa-regular fa-check";
    }
    if (props.invalid) {
      return "fa-regular fa-exclamation-circle";
    }
    return null;
  });

  const spin = computed(() => {
    return props.location === "suffix" && props.loading;
  });

  const internalColor = computed(() => {
    if (props.location === "suffix") {
      const isShowingValidationState = props.valid && props.showValidState;
      return isShowingValidationState ? "success" : props.color;
    }
    return props.color;
  });

  const icon = computed(() => {
    if (props.icon) {
      return props.icon;
    }
    if (props.location === "prefix") {
      return props.prefixIcon;
    }
    return suffixIcon.value;
  });

  const isClickable = computed(() => {
    return (
      (props.location === "prefix" && props.prefixIconButton) ||
      (props.location === "suffix" && props.suffixIconButton)
    );
  });

  function click() {
    if (!isClickable.value || props.disabled) {
      return;
    }
    if (props.location === "prefix") {
      emit("clickPrefixIcon");
    }
    if (props.location === "suffix") {
      emit("clickSuffixIcon");
    }
  }

  const disabled = computed(() => isClickable.value && props.disabled);

  const tag = computed(() => (isClickable.value ? "button" : "span"));
  const type = computed(() => (isClickable.value ? "button" : null));

  return {
    wrapper: {
      props: reactive({
        type,
        disabled,
      }),
      on: { click },
    },
    icon,
    tag,
    location: toRef(() => props.location),
    spin,
    color: internalColor,
    size: toRef(() => props.size),
    isClickable,
    disabled,
  };
}

export type { UseInputIconAtomEmit, UseInputIconAtomProps };
export { emits, props, scoped, use };
